import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, unref as _unref, normalizeStyle as _normalizeStyle, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "p-4 sm:p-6" }
const _hoisted_2 = { class: "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8" }
const _hoisted_3 = { class: "bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700" }
const _hoisted_4 = { class: "flex items-center justify-between" }
const _hoisted_5 = { class: "text-3xl font-bold text-gray-900 dark:text-white mt-2" }
const _hoisted_6 = { class: "bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700" }
const _hoisted_7 = { class: "flex items-center justify-between" }
const _hoisted_8 = { class: "text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2" }
const _hoisted_9 = { class: "bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700" }
const _hoisted_10 = { class: "flex items-center justify-between" }
const _hoisted_11 = { class: "text-3xl font-bold text-green-600 dark:text-green-400 mt-2" }
const _hoisted_12 = { class: "bg-white dark:bg-black rounded-lg shadow-lg p-6 border border-gray-200 dark:border-gray-700" }
const _hoisted_13 = { class: "flex items-center justify-between" }
const _hoisted_14 = { class: "text-3xl font-bold text-red-600 dark:text-red-400 mt-2" }
const _hoisted_15 = { class: "bg-white dark:bg-black rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700" }
const _hoisted_16 = { key: 0 }
const _hoisted_17 = {
  key: 1,
  class: "text-center py-16 px-6"
}
const _hoisted_18 = { class: "text-red-500 dark:text-red-400 mb-6 max-w-sm mx-auto" }
const _hoisted_19 = { key: 2 }
const _hoisted_20 = {
  key: 3,
  class: "text-center py-16 px-6"
}
const _hoisted_21 = { class: "text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3" }
const _hoisted_22 = { class: "text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 max-w-sm mx-auto" }
const _hoisted_23 = { key: 4 }

import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useAdminTireStore } from '@/stores/adminTireStore';
import { useBrand } from '@/composables/brand/useBrand';
import type { Tire } from '@/types/tire';
import TireApprovalFilters from '@/components/admin/TireApprovalFilters.vue';
import TireApprovalTable from '@/components/admin/TireApprovalTable.vue';
import CustomerPagination from '@/components/admin/CustomerPagination.vue';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';
import TireDetailsModal from '@/components/admin/TireDetailsModal.vue';
import ApproveTireModal from '@/components/admin/ApproveTireModal.vue';
import RejectTireModal from '@/components/admin/RejectTireModal.vue';
import DeleteTireModal from '@/components/admin/DeleteTireModal.vue';
import EditAdminTireModal from '@/components/admin/EditAdminTireModal.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'TireApprovalsView',
  setup(__props) {

const toast = useToast();
const tireStore = useAdminTireStore();
const { brandColors } = useBrand();

const searchQuery = ref('');
const statusFilter = ref('');
const brandFilter = ref('');
const showDetailsModal = ref(false);
const showEditModal = ref(false);
const showApproveModal = ref(false);
const showRejectModal = ref(false);
const showDeleteModal = ref(false);
const selectedTire = ref<Tire | null>(null);
const tireToEdit = ref<Tire | null>(null);
const tireToApprove = ref<Tire | null>(null);
const tireToReject = ref<Tire | null>(null);
const tireToDelete = ref<Tire | null>(null);

const tires = computed(() => tireStore.tires);
const loading = computed(() => tireStore.loading);
const error = computed(() => tireStore.error);
const pagination = computed(() => tireStore.pagination);
const pendingTires = computed(() => tireStore.pendingTires);
const approvedTires = computed(() => tireStore.approvedTires);
const rejectedTires = computed(() => tireStore.rejectedTires);

function applyFilters() {
  tireStore.setFilters({
    search: searchQuery.value,
    status: statusFilter.value,
    brand: brandFilter.value ? Number(brandFilter.value) : undefined,
    page: 1,
    per_page: 10
  });
  tireStore.fetchTires();
}

function resetFilters() {
  searchQuery.value = '';
  statusFilter.value = '';
  brandFilter.value = '';
  tireStore.resetFilters();
  tireStore.fetchTires();
}

function goToPreviousPage() {
  if (pagination.value.current_page > 1) {
    tireStore.setFilters({ page: pagination.value.current_page - 1 });
    tireStore.fetchTires();
  }
}

function goToNextPage() {
  if (pagination.value.current_page < pagination.value.last_page) {
    tireStore.setFilters({ page: pagination.value.current_page + 1 });
    tireStore.fetchTires();
  }
}

function goToPage(page: number) {
  tireStore.setFilters({ page });
  tireStore.fetchTires();
}

function viewTire(tire: Tire) {
  selectedTire.value = tire;
  showDetailsModal.value = true;
}

function closeDetailsModal() {
  showDetailsModal.value = false;
  selectedTire.value = null;
}

function openEditModal(tire: Tire) {
  tireToEdit.value = tire;
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  tireToEdit.value = null;
}

async function handleEditTire(
  data: { pattern: string; tire_size: string; dot: string },
  newTirePhotos: File[],
  deletedTirePhotoIds: number[]
) {
  if (!tireToEdit.value) return;

  try {
    await tireStore.updateTire(tireToEdit.value.tire_id, data, newTirePhotos, deletedTirePhotoIds);
    toast.success('Tire updated successfully');
    closeEditModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to update tire');
  }
}

function openApproveModal(tire: Tire) {
  tireToApprove.value = tire;
  showApproveModal.value = true;
}

function closeApproveModal() {
  showApproveModal.value = false;
  tireToApprove.value = null;
}

async function confirmApprove() {
  if (!tireToApprove.value) return;

  try {
    await tireStore.approveTire(tireToApprove.value.tire_id);
    toast.success('Tire approved successfully');
    closeApproveModal();
  } catch (err) {
    toast.error('Failed to approve tire');
  }
}

function openRejectModal(tire: Tire) {
  tireToReject.value = tire;
  showRejectModal.value = true;
}

function closeRejectModal() {
  showRejectModal.value = false;
  tireToReject.value = null;
}

async function confirmReject(rejectionReason: string) {
  if (!tireToReject.value) return;

  try {
    await tireStore.rejectTire(tireToReject.value.tire_id, rejectionReason);
    toast.success('Tire rejected');
    closeRejectModal();
  } catch (err) {
    toast.error('Failed to reject tire');
  }
}

function openDeleteModal(tire: Tire) {
  tireToDelete.value = tire;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  tireToDelete.value = null;
}

async function confirmDelete() {
  if (!tireToDelete.value) return;

  try {
    await tireStore.deleteTire(tireToDelete.value.tire_id);
    toast.success('Tire deleted successfully');
    closeDeleteModal();
  } catch (err) {
    toast.error('Failed to delete tire');
  }
}

async function handleExport() {
  try {
    await tireStore.exportTires();
  } catch (error: any) {
    if (error.message === 'No tires to export with current filters') {
      toast.warning('No tires to export with current filters');
    } else {
      toast.error('Failed to export tires. Please try again.');
    }
  }
}

onMounted(() => {
  tireStore.resetFilters();
  tireStore.fetchTires();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[15] || (_cache[15] = _createElementVNode("div", { class: "mb-6 sm:mb-8" }, [
        _createElementVNode("h1", { class: "text-2xl font-bold text-gray-900 dark:text-white" }, "Tire Approvals")
      ], -1)),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", null, [
              _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-sm font-medium text-gray-600 dark:text-gray-400" }, "Total Tires", -1)),
              _createElementVNode("p", _hoisted_5, _toDisplayString(pagination.value.total || 0), 1)
            ]),
            _cache[5] || (_cache[5] = _createElementVNode("div", { class: "w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center" }, [
              _createElementVNode("svg", {
                class: "w-6 h-6 text-blue-600 dark:text-blue-400",
                fill: "currentColor",
                viewBox: "0 0 20 20"
              }, [
                _createElementVNode("path", { d: "M10 2a8 8 0 100 16 8 8 0 000-16zM8 9a1 1 0 112 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z" })
              ])
            ], -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", null, [
              _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-sm font-medium text-gray-600 dark:text-gray-400" }, "Pending", -1)),
              _createElementVNode("p", _hoisted_8, _toDisplayString(pendingTires.value.length), 1)
            ]),
            _cache[7] || (_cache[7] = _createElementVNode("div", { class: "w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center" }, [
              _createElementVNode("svg", {
                class: "w-6 h-6 text-yellow-600 dark:text-yellow-400",
                fill: "currentColor",
                viewBox: "0 0 20 20"
              }, [
                _createElementVNode("path", {
                  "fill-rule": "evenodd",
                  d: "M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z",
                  "clip-rule": "evenodd"
                })
              ])
            ], -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", null, [
              _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-sm font-medium text-gray-600 dark:text-gray-400" }, "Approved", -1)),
              _createElementVNode("p", _hoisted_11, _toDisplayString(approvedTires.value.length), 1)
            ]),
            _cache[9] || (_cache[9] = _createElementVNode("div", { class: "w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center" }, [
              _createElementVNode("svg", {
                class: "w-6 h-6 text-green-600 dark:text-green-400",
                fill: "currentColor",
                viewBox: "0 0 20 20"
              }, [
                _createElementVNode("path", {
                  "fill-rule": "evenodd",
                  d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
                  "clip-rule": "evenodd"
                })
              ])
            ], -1))
          ])
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("div", null, [
              _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-sm font-medium text-gray-600 dark:text-gray-400" }, "Rejected", -1)),
              _createElementVNode("p", _hoisted_14, _toDisplayString(rejectedTires.value.length), 1)
            ]),
            _cache[11] || (_cache[11] = _createElementVNode("div", { class: "w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center" }, [
              _createElementVNode("svg", {
                class: "w-6 h-6 text-red-600 dark:text-red-400",
                fill: "currentColor",
                viewBox: "0 0 20 20"
              }, [
                _createElementVNode("path", {
                  "fill-rule": "evenodd",
                  d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
                  "clip-rule": "evenodd"
                })
              ])
            ], -1))
          ])
        ])
      ]),
      _createVNode(TireApprovalFilters, {
        search: searchQuery.value,
        "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
        status: statusFilter.value,
        "onUpdate:status": _cache[1] || (_cache[1] = ($event: any) => ((statusFilter).value = $event)),
        brand: brandFilter.value,
        "onUpdate:brand": _cache[2] || (_cache[2] = ($event: any) => ((brandFilter).value = $event)),
        onApply: applyFilters,
        onReset: resetFilters,
        onExport: handleExport
      }, null, 8, ["search", "status", "brand"]),
      _createElementVNode("div", _hoisted_15, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
              _createVNode(SkeletonLoader, {
                type: "table",
                rows: 5,
                columns: 9
              }),
              _createVNode(SkeletonLoader, {
                type: "card",
                rows: 5
              })
            ]))
          : (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                _cache[12] || (_cache[12] = _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  class: "mx-auto h-16 w-16 text-red-400 mb-4",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "1",
                    d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  })
                ], -1)),
                _cache[13] || (_cache[13] = _createElementVNode("h3", { class: "text-lg font-medium text-gray-900 dark:text-white mb-2" }, "Error loading tires", -1)),
                _createElementVNode("p", _hoisted_18, _toDisplayString(error.value), 1),
                _createElementVNode("button", {
                  class: "bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-sm",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(tireStore).fetchTires()))
                }, " Try Again ")
              ]))
            : (tires.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                  _createVNode(TireApprovalTable, {
                    tires: tires.value,
                    loading: loading.value,
                    onView: viewTire,
                    onEdit: openEditModal,
                    onApprove: openApproveModal,
                    onReject: openRejectModal,
                    onDelete: openDeleteModal
                  }, null, 8, ["tires", "loading"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_20, [
                  _createElementVNode("div", {
                    class: "w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-full flex items-center justify-center",
                    style: _normalizeStyle({ backgroundColor: _unref(brandColors).primary + '20' })
                  }, [
                    (_openBlock(), _createElementBlock("svg", {
                      xmlns: "http://www.w3.org/2000/svg",
                      class: "h-10 w-10 sm:h-12 sm:w-12",
                      style: _normalizeStyle({ color: _unref(brandColors).primary }),
                      fill: "none",
                      viewBox: "0 0 24 24",
                      stroke: "currentColor",
                      "stroke-width": "2"
                    }, _cache[14] || (_cache[14] = [
                      _createElementVNode("path", {
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        d: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      }, null, -1)
                    ]), 4))
                  ], 4),
                  _createElementVNode("h3", _hoisted_21, _toDisplayString(searchQuery.value || statusFilter.value || brandFilter.value ? 'No Tires Match Your Search' : 'No Tires Found'), 1),
                  _createElementVNode("p", _hoisted_22, _toDisplayString(searchQuery.value || statusFilter.value || brandFilter.value ? 'Try adjusting your filters to find what you\'re looking for.' : 'No tire registrations have been submitted yet.'), 1)
                ])),
        (tires.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
              _createVNode(CustomerPagination, {
                "current-page": pagination.value.current_page,
                "last-page": pagination.value.last_page,
                "per-page": pagination.value.per_page,
                total: pagination.value.total,
                onPrev: goToPreviousPage,
                onNext: goToNextPage,
                onGoto: goToPage
              }, null, 8, ["current-page", "last-page", "per-page", "total"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(TireDetailsModal, {
      "is-open": showDetailsModal.value,
      tire: selectedTire.value,
      onClose: closeDetailsModal
    }, null, 8, ["is-open", "tire"]),
    _createVNode(EditAdminTireModal, {
      "is-open": showEditModal.value,
      tire: tireToEdit.value,
      loading: loading.value,
      onClose: closeEditModal,
      onSave: handleEditTire
    }, null, 8, ["is-open", "tire", "loading"]),
    _createVNode(ApproveTireModal, {
      "is-open": showApproveModal.value,
      tire: tireToApprove.value,
      loading: loading.value,
      onClose: closeApproveModal,
      onConfirm: confirmApprove
    }, null, 8, ["is-open", "tire", "loading"]),
    _createVNode(RejectTireModal, {
      "is-open": showRejectModal.value,
      tire: tireToReject.value,
      loading: loading.value,
      onClose: closeRejectModal,
      onConfirm: confirmReject
    }, null, 8, ["is-open", "tire", "loading"]),
    _createVNode(DeleteTireModal, {
      "is-open": showDeleteModal.value,
      tire: tireToDelete.value,
      loading: loading.value,
      onClose: closeDeleteModal,
      onConfirm: confirmDelete
    }, null, 8, ["is-open", "tire", "loading"])
  ], 64))
}
}

})